import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { supabase } from '../../../api/supabase'
import WarehouseLayout from '../WarehouseLayout'
import { fmtDate } from '../types'
import { useStickyHScroll } from '../../../hooks/useStickyHScroll'
import { StickyHScrollbar } from '../../../components/StickyHScrollbar'

interface InventoryItem {
  id: string
  job_ref: string | null
  group_name: string | null
  hbl_no: string | null
  system_ref: string | null
  description: string | null
  delivery_address: string | null
  delivery_method: string | null
  cartons: number
  pallets_qty: number
  cbm: number | null
  kgs: number | null
  status: 'in_stock' | 'outbound' | 'cleared'
  location_code: string | null
  inbound_at: string | null
}

const STATUS_COLORS: Record<string, string> = {
  in_stock: 'bg-green-100 text-green-700',
  outbound: 'bg-yellow-100 text-yellow-700',
  cleared:  'bg-gray-100 text-gray-500',
}

export default function InventoryPage() {
  const { id } = useParams<{ id: string }>()
  const [items, setItems] = useState<InventoryItem[]>([])
  const [loading, setLoading] = useState(true)
  const [filterStatus, setFilterStatus] = useState('')
  const [filterJobRef, setFilterJobRef] = useState('')
  const [filterDateFrom, setFilterDateFrom] = useState('')
  const [filterDateTo, setFilterDateTo] = useState('')

  useEffect(() => {
    async function load() {
      setLoading(true)
      let q = supabase.from('warehouse_inventory').select('*').eq('warehouse_id', id!).order('inbound_at', { ascending: false })
      if (filterStatus) q = q.eq('status', filterStatus)
      if (filterJobRef) q = q.ilike('job_ref', `%${filterJobRef}%`)
      if (filterDateFrom) q = q.gte('inbound_at', filterDateFrom)
      if (filterDateTo) q = q.lte('inbound_at', filterDateTo + 'T23:59:59Z')
      const { data } = await q
      setItems((data as InventoryItem[]) || [])
      setLoading(false)
    }
    load()
  }, [id, filterStatus, filterJobRef, filterDateFrom, filterDateTo])

  const totalCtns = items.reduce((s, it) => s + it.cartons, 0)
  const totalPlts = items.reduce((s, it) => s + it.pallets_qty, 0)
  const hasFilters = !!(filterStatus || filterJobRef || filterDateFrom || filterDateTo)

  const { scrollProps: invScrollProps, contentRef: invTableRef, bar: invScrollBar } = useStickyHScroll([items])

  return (
    <WarehouseLayout warehouseId={id!}>
      <div className="space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Inbound</h2>
          <div className="flex gap-4 text-sm text-gray-500">
            <span>Total: <strong className="text-gray-800 dark:text-gray-100 font-mono">{totalCtns}</strong> ctns</span>
            <span><strong className="text-gray-800 dark:text-gray-100 font-mono">{totalPlts}</strong> pallets</span>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 px-4 py-3 flex flex-wrap gap-3 items-end">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Job Ref</label>
            <input
              className="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-1.5 text-sm bg-white dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 w-32"
              value={filterJobRef} onChange={e => setFilterJobRef(e.target.value)} placeholder="KI/KE…" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Status</label>
            <select className="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-1.5 text-sm bg-white dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
              <option value="">All</option>
              <option value="in_stock">In Stock</option>
              <option value="outbound">Outbound</option>
              <option value="cleared">Cleared</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Inbound From</label>
            <input type="date" className="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-1.5 text-sm bg-white dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={filterDateFrom} onChange={e => setFilterDateFrom(e.target.value)} />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">To</label>
            <input type="date" className="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-1.5 text-sm bg-white dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={filterDateTo} onChange={e => setFilterDateTo(e.target.value)} />
          </div>
          {hasFilters && (
            <button onClick={() => { setFilterStatus(''); setFilterJobRef(''); setFilterDateFrom(''); setFilterDateTo('') }}
              className="text-xs text-gray-400 hover:text-gray-600 underline self-end pb-1.5">
              Clear filters
            </button>
          )}
        </div>

        {/* Table */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="overflow-x-auto" {...invScrollProps}>
            {loading ? (
              <div className="py-16 text-center text-gray-400">Loading…</div>
            ) : items.length === 0 ? (
              <div className="py-16 text-center text-gray-400">
                {hasFilters ? 'No records match these filters.' : 'No inventory records yet. Records are created when a devanning is completed.'}
              </div>
            ) : (
              <table ref={invTableRef as React.RefObject<HTMLTableElement>} className="min-w-full text-sm divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    {['JOB REF', 'GROUP', 'HBL', 'DESCRIPTION', 'DELIVERY ADDR', 'CTNS', 'PALLETS', 'INBOUND DATE', 'LOCATION', 'STATUS'].map(h => (
                      <th key={h} className="px-3 py-2.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {items.map(item => (
                    <tr key={item.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                      <td className="px-3 py-2 font-mono text-xs text-blue-700 dark:text-blue-400 font-semibold whitespace-nowrap">{item.job_ref || '—'}</td>
                      <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-300 whitespace-nowrap">{item.group_name || '—'}</td>
                      <td className="px-3 py-2 text-xs font-mono text-gray-700 dark:text-gray-200 whitespace-nowrap">{item.hbl_no || '—'}</td>
                      <td className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400 max-w-[200px] truncate" title={item.description || ''}>{item.description || '—'}</td>
                      <td className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400 max-w-[160px] truncate" title={item.delivery_address || ''}>{item.delivery_address || '—'}</td>
                      <td className="px-3 py-2 text-xs font-semibold font-mono text-center">{item.cartons}</td>
                      <td className="px-3 py-2 text-xs font-semibold font-mono text-center">{item.pallets_qty || '—'}</td>
                      <td className="px-3 py-2 text-xs text-gray-500 whitespace-nowrap">{item.inbound_at ? fmtDate(item.inbound_at) : '—'}</td>
                      <td className="px-3 py-2 text-xs text-gray-500">{item.location_code || '—'}</td>
                      <td className="px-3 py-2">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[item.status] || 'bg-gray-100 text-gray-500'}`}>
                          {item.status.replace('_', ' ')}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
      <StickyHScrollbar {...invScrollBar} />
    </WarehouseLayout>
  )
}
